import { Flex } from "@chakra-ui/react";
import React from "react";

import type { TokenTransfer } from "types/api/tokenTransfer";
import type { ChainConfig } from "types/multichain";

import getCurrencyValue from "lib/getCurrencyValue";
import { getTokenTypeName } from "lib/token/tokenTypes";
import { Badge } from "toolkit/chakra/badge";
import { Skeleton } from "toolkit/chakra/skeleton";
import AddressFromTo from "ui/shared/address/AddressFromTo";
import NftEntity from "ui/shared/entities/nft/NftEntity";
import TokenEntity, {
  getTokenTransferDisplayName,
} from "ui/shared/entities/token/TokenEntity";
import TxEntity from "ui/shared/entities/tx/TxEntity";
import ListItemMobile from "ui/shared/ListItemMobile/ListItemMobile";
import TimeWithTooltip from "ui/shared/time/TimeWithTooltip";
import TxFee from "ui/shared/tx/TxFee";
import { getTokenTransferTypeText } from "ui/shared/TokenTransfer/helpers";
import TxAdditionalInfo from "ui/txs/TxAdditionalInfo";

type Props = TokenTransfer & {
  baseAddress?: string;
  showTxInfo?: boolean;
  enableTimeIncrement?: boolean;
  isLoading?: boolean;
  chainData?: ChainConfig;
};

const TokenTransferListItem = ({
  token,
  total,
  transaction_hash: txHash,
  from,
  to,
  baseAddress,
  showTxInfo,
  type,
  timestamp,
  enableTimeIncrement,
  isLoading,
  chainData,
  fee,
  fee_token_id,
  fee_token_symbol,
  fee_token_address,
  fee_token_name,
  fee_token_decimals,
  fee_rate,
  token_scale,
  exchange_rate,
}: Props) => {
  // 构建用于 TxFee 组件的交易对象
  const txForFee = {
    fee: fee || { type: "actual", value: null },
    fee_token_id,
    fee_token_symbol,
    fee_token_address,
    fee_token_name,
    fee_token_decimals,
    fee_rate,
    token_scale,
    exchange_rate,
  };
  const { usd, valueStr } =
    total && "value" in total && total.value !== null
      ? getCurrencyValue({
          value: total.value,
          exchangeRate: token?.exchange_rate,
          accuracy: 8,
          accuracyUsd: 2,
          decimals: total.decimals || "0",
        })
      : { usd: null, valueStr: null };

  return (
    <ListItemMobile rowGap={3}>
      <Flex w="100%" justifyContent="space-between">
        <Flex
          flexWrap="wrap"
          rowGap={1}
          mr={showTxInfo && txHash ? 2 : 0}
          columnGap={2}
          overflow="hidden"
        >
          {token && (
            <>
              <TokenEntity
                token={{
                  ...token,
                  name: getTokenTransferDisplayName(token.address, token.name, {
                    symbol: token.symbol,
                  }),
                }}
                isLoading={isLoading}
                noSymbol
                noCopy
                preserveName
                w="auto"
              />
              <Badge flexShrink={0} loading={isLoading}>
                {getTokenTypeName(token.type)}
              </Badge>
            </>
          )}
          <Badge colorPalette="orange" loading={isLoading}>
            {getTokenTransferTypeText(type)}
          </Badge>
        </Flex>
        {showTxInfo && txHash && (
          <TxAdditionalInfo hash={txHash} isMobile isLoading={isLoading} />
        )}
      </Flex>
      {total && "token_id" in total && total.token_id !== null && token && (
        <NftEntity
          hash={token.address_hash}
          id={total.token_id}
          instance={total.token_instance}
          isLoading={isLoading}
        />
      )}
      {showTxInfo && txHash && (
        <Flex
          justifyContent="space-between"
          alignItems="center"
          lineHeight="24px"
          width="100%"
        >
          <TxEntity
            isLoading={isLoading}
            hash={txHash}
            truncation="constant_long"
            fontWeight="700"
            chain={chainData}
          />
          <TimeWithTooltip
            timestamp={timestamp}
            enableIncrement={enableTimeIncrement}
            isLoading={isLoading}
            color="text.secondary"
            fontWeight="400"
            fontSize="sm"
          />
        </Flex>
      )}
      <AddressFromTo
        from={from}
        to={to}
        current={baseAddress}
        isLoading={isLoading}
        w="100%"
      />
      {valueStr && (
        <Flex columnGap={2} w="100%">
          <Skeleton loading={isLoading} fontWeight={500} flexShrink={0}>
            Value
          </Skeleton>
          <Skeleton
            loading={isLoading}
            color="text.secondary"
            wordBreak="break-all"
            overflow="hidden"
          >
            <span>{valueStr}</span>
            {usd && <span> (${usd})</span>}
          </Skeleton>
        </Flex>
      )}
      {/* { fee_token_id !== undefined && fee_token_symbol && fee_rate ? (
        <Flex columnGap={ 2 } w="100%">
          <Skeleton loading={ isLoading } fontWeight={ 500 } flexShrink={ 0 }>Fee Token</Skeleton>
          <TxFee fixedDecimals={ 8 } tx={ txForFee as any } isLoading={ isLoading } accuracy={ 8 } justifyContent="end" />
        </Flex>
      ) : (
        fee?.value && (
          <Flex columnGap={ 2 } w="100%">
            <Skeleton loading={ isLoading } fontWeight={ 500 } flexShrink={ 0 }>Fee</Skeleton>
            <TxFee fixedDecimals={ 8 } tx={ txForFee as any } isLoading={ isLoading } accuracy={ 8 } justifyContent="end" />
          </Flex>
        )
      ) } */}
    </ListItemMobile>
  );
};

export default React.memo(TokenTransferListItem);
